import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import StreamingIndicator from './StreamingIndicator';

import {
  Clock, Link, ExternalLink, ChevronRight, AlertTriangle,
  Globe, AlertCircle, FileText, Hash, Plus,
  TrendingUp, BookmarkPlus, LineChart, Box, Copy, Check, Paperclip,
  ChevronDown, ChevronUp, ArrowUp, ArrowDown, Maximize2, Minimize2, Search, BarChart3
} from 'lucide-react';
import { PromptChip, FileChip } from './WelcomeStatePreview';
import { ChatMessage, SearchResult, ResearchSource } from '../../types/chat';
import { useStockView } from '../../contexts/StockViewContext';
import { useWatchlists } from '../../hooks/useWatchlists';
import { api } from '../../services/api';
import { useExpandedStocks } from '../../contexts/ExpandedStocksContext';
import { useChartSync } from '../../contexts/ChartSyncContext';

interface MessageBlockProps {
  message: ChatMessage;
  styles: Record<string, string>;
  tickers?: string[];
  formatTimestamp: (timestamp: string | Date) => string;
}

interface NewsSourcesIndicatorProps {
  sources: NonNullable<ChatMessage['news']>;
}

interface WebSearchResultsProps {
  results: NonNullable<ChatMessage['webSearchResults']>;
}

interface ResearchSourcesProps {
  sources: NonNullable<ChatMessage['researchSources']>;
  searchQueries?: {
    query: string;
    timestamp: string;
    resultsCount: number;
  }[];
}

// Define interfaces for stock data
interface StockPriceData {
  price: number;
  change: number;
  changePercent: number;
  high: number;
  low: number;
  open: number;
  prevClose: number;
  volume: number;
  marketCap?: number;
  timestamp?: string;
}

interface StockChartData {
  timestamps: string[];
  prices: number[];
}

const EnhancedStockChip: React.FC<{
  ticker: string;
  companyName?: string;
  prefix?: string;
  isDarkMode: boolean;
  onDataLoaded?: () => void;
}> = ({ ticker, companyName, prefix, isDarkMode, onDataLoaded }) => {
  const [expanded, setExpanded] = useState(false);
  const [expandedChart, setExpandedChart] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [priceData, setPriceData] = useState<StockPriceData | null>(null);
  const [chartData, setChartData] = useState<StockChartData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isValidStock, setIsValidStock] = useState<boolean | null>(null);
  const { openStockModal } = useStockView();
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [fetchedCompanyName, setFetchedCompanyName] = useState<string | null>(null);
  const { expandedStocks, toggleStockExpansion, getStockPosition } = useExpandedStocks();
  const { selectedTimeframe, setSelectedTimeframe, hoveredPoint, setHoveredPoint, 
          isDragging, setIsDragging, dragStart, setDragStart, dragEnd, setDragEnd } = useChartSync();
  const [tooltipData, setTooltipData] = useState<{
    price: number;
    timestamp: string;
    x: number;
    y: number;
  } | null>(null);
  const [priceRange, setPriceRange] = useState<{ min: number; max: number } | null>(null);
  
  // Get the position from context if expanded
  const position = expanded ? getStockPosition(ticker) : null;

  // Available timeframes with friendly display names
  const timeframes = [
    { value: '1d', label: '1D' },
    { value: '5d', label: '5D' },
    { value: '1mo', label: '1M' },
    { value: '3mo', label: '3M' },
    { value: '6mo', label: '6M' },
    { value: '1y', label: '1Y' },
  ];

  // Fetch stock price data immediately when component mounts or timeframe changes
  useEffect(() => {
    const fetchStockData = async () => {
      if (!ticker) return;
      
      setIsLoading(true);
      setLoadError(null);
      
      try {
        const response = await api.get(`/market/stock/${ticker}`, {
          params: { timeframe: selectedTimeframe }
        });
        
        if (response.data && response.data.results && response.data.results.length > 0) {
          const latestData = response.data.results[response.data.results.length - 1];
          const prevClose = response.data.previousClose || latestData.o;
          
          setPriceData({
            price: latestData.c,
            change: latestData.c - prevClose,
            changePercent: ((latestData.c - prevClose) / prevClose) * 100,
            high: latestData.h,
            low: latestData.l,
            open: latestData.o,
            prevClose: prevClose,
            volume: latestData.v,
            timestamp: new Date().toISOString()
          });
          
          // If expanded, also prepare chart data
          const timestamps = response.data.results.map((bar: any) => new Date(bar.t).toISOString());
          const prices = response.data.results.map((bar: any) => bar.c);
          
          setChartData({
            timestamps,
            prices
          });

          // Calculate price range for Y-axis markers
          if (prices && prices.length > 0) {
            const minPrice = Math.min(...prices) * 0.995;
            const maxPrice = Math.max(...prices) * 1.005;
            setPriceRange({ min: minPrice, max: maxPrice });
          }
          
          // Valid stock with data
          setIsValidStock(true);
        } else {
          // No data returned, likely not a valid stock ticker
          console.log(`No stock data found for ticker: ${ticker}`);
          setIsValidStock(false);
        }
      } catch (error) {
        console.error('Failed to fetch stock data:', error);
        setLoadError('Failed to load stock data');
        setIsValidStock(false); // Consider it invalid if we can't fetch data
      } finally {
        setIsLoading(false);
        if (onDataLoaded) onDataLoaded();
      }
    };
    
    fetchStockData();
  }, [ticker, selectedTimeframe, onDataLoaded]);

  // Fetch company name if not provided
  useEffect(() => {
    const fetchCompanyName = async () => {
      if (!ticker || companyName || !isValidStock) return;
      
      try {
        const { data } = await api.get(`/market/stock/${ticker}/details`);
        if (data && data.name) {
          setFetchedCompanyName(data.name);
        }
      } catch (error) {
        console.error('Failed to fetch company name:', error);
      }
    };
    
    fetchCompanyName();
  }, [ticker, companyName, isValidStock]);

  // Draw chart when data is available
  useEffect(() => {
    if (chartData && expanded && chartRef.current) {
      const canvas = chartRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      
      // Fix for high DPI displays - get device pixel ratio
      const devicePixelRatio = window.devicePixelRatio || 1;
      
      // Get the canvas size from its computed style
      const rect = canvas.getBoundingClientRect();
      const displayWidth = rect.width;
      const displayHeight = rect.height;
      
      // Set the canvas dimensions to match display size * device pixel ratio
      // This ensures sharp rendering on high DPI screens
      if (canvas.width !== displayWidth * devicePixelRatio || 
          canvas.height !== displayHeight * devicePixelRatio) {
        // Set canvas internal dimensions to its display size * devicePixelRatio
        canvas.width = displayWidth * devicePixelRatio;
        canvas.height = displayHeight * devicePixelRatio;
        
        // Scale the context to ensure correct drawing operations
        ctx.scale(devicePixelRatio, devicePixelRatio);
        
        // Set CSS size explicitly to maintain layout
        canvas.style.width = `${displayWidth}px`;
        canvas.style.height = `${displayHeight}px`;
      }
      
      // Clear previous chart with the properly scaled dimensions
      ctx.clearRect(0, 0, displayWidth, displayHeight);
      
      const prices = chartData.prices;
      const timestamps = chartData.timestamps;
      const width = displayWidth;
      const height = displayHeight;
      const padding = { left: 40, right: 50, top: 10, bottom: 20 };
      
      // Skip rendering if we have no valid prices
      if (!prices || prices.length === 0) {
        return;
      }
      
      // Find min/max for scaling
      const minPrice = Math.min(...prices) * 0.995;
      const maxPrice = Math.max(...prices) * 1.005;
      const priceRange = maxPrice - minPrice;
      
      // Skip rendering if the price range is invalid
      if (priceRange <= 0 || !isFinite(priceRange)) {
        console.error('Invalid price range for chart:', { minPrice, maxPrice, priceRange });
        return;
      }
      
      // Style
      const positiveColor = isDarkMode ? '#10b981' : '#047857'; // green for positive
      const negativeColor = isDarkMode ? '#ef4444' : '#b91c1c'; // red for negative
      const lineColor = priceData && priceData.change >= 0 ? positiveColor : negativeColor;
      
      // Use crisp lines by aligning to pixel grid
      const drawCrispLine = (x1: number, y1: number, x2: number, y2: number) => {
        ctx.beginPath();
        ctx.moveTo(Math.floor(x1) + 0.5, Math.floor(y1) + 0.5);
        ctx.lineTo(Math.floor(x2) + 0.5, Math.floor(y2) + 0.5);
        ctx.stroke();
      };
      
      // Configure text rendering for sharpness
      ctx.textBaseline = 'middle';
      // Higher quality font rendering
      ctx.font = '10px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      
      // Draw Y-axis
      ctx.strokeStyle = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
      ctx.lineWidth = 1;
      drawCrispLine(padding.left, padding.top, padding.left, height - padding.bottom);
      
      // Draw Y-axis price markers (3 price points)
      const pricePoints = 3; // Number of price points to show
      ctx.font = '10px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillStyle = isDarkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)';
      
      for (let i = 0; i <= pricePoints - 1; i++) {
        const y = padding.top + ((height - padding.top - padding.bottom) * i) / (pricePoints - 1);
        const price = maxPrice - (i / (pricePoints - 1)) * (maxPrice - minPrice);
        
        // Draw grid line
        ctx.strokeStyle = isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)';
        drawCrispLine(padding.left, y, width - padding.right, y);
        
        // Print price on both sides for better readability
        ctx.textAlign = 'right';
        ctx.fillText(formatPrice(price), padding.left - 5, y);
        
        // Right side price markers
        ctx.textAlign = 'left';
        ctx.fillText(formatPrice(price), width - padding.right + 5, y);
      }
      
      // Draw the line chart
      ctx.beginPath();
      ctx.strokeStyle = lineColor;
      ctx.lineWidth = 2;
      
      // Calculate point coordinates
      const points = prices.map((price, i) => {
        const x = padding.left + ((width - padding.left - padding.right) * i) / (prices.length - 1);
        const y = height - padding.bottom - ((price - minPrice) / priceRange) * (height - padding.top - padding.bottom);
        return { x, y, price, timestamp: timestamps[i] };
      });
      
      // Draw main chart line with antialiasing
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      points.forEach((point, i) => {
        if (i === 0) {
          ctx.moveTo(point.x, point.y);
        } else {
          ctx.lineTo(point.x, point.y);
        }
      });
      ctx.stroke();
      
      // Add gradient fill under the line
      const gradient = ctx.createLinearGradient(0, 0, 0, height);
      if (priceData && priceData.change >= 0) {
        gradient.addColorStop(0, 'rgba(16, 185, 129, 0.2)');
        gradient.addColorStop(1, 'rgba(16, 185, 129, 0)');
      } else {
        gradient.addColorStop(0, 'rgba(239, 68, 68, 0.2)');
        gradient.addColorStop(1, 'rgba(239, 68, 68, 0)');
      }
      
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.moveTo(padding.left, height - padding.bottom); // Bottom left
      
      points.forEach((point) => {
        ctx.lineTo(point.x, point.y);
      });
      
      ctx.lineTo(width - padding.right, height - padding.bottom); // Bottom right
      ctx.closePath();
      ctx.fill();
      
      // Add time markers on x-axis for longer timeframes
      if (['1mo', '3mo', '6mo', '1y'].includes(selectedTimeframe) && prices.length > 0) {
        ctx.fillStyle = isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)';
        ctx.font = '10px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        
        // Show 3-5 date markers depending on width
        const markerCount = width < 300 ? 3 : 5;
        const interval = Math.floor(prices.length / (markerCount - 1));
        
        for (let i = 0; i < prices.length; i += interval) {
          if (i >= prices.length) continue;
          
          const x = padding.left + ((width - padding.left - padding.right) * i) / (prices.length - 1);
          const date = new Date(timestamps[i]);
          const dateLabel = `${date.getMonth() + 1}/${date.getDate()}`;
          
          if (i > 0 && i < prices.length - 1) {
            ctx.fillText(dateLabel, x, height - 5);
          }
        }
      }
      
      // If there's a hovered point from another chart, draw a vertical line at the same timestamp position
      if (hoveredPoint && !isDragging) {
        // Find the closest matching timestamp point in this chart
        let relativeX = 0;
        const hoverTime = new Date(hoveredPoint.timestamp).getTime();
        
        // Find the closest timestamp
        let closestPointIndex = 0;
        let smallestTimeDiff = Infinity;
        
        timestamps.forEach((timestamp, i) => {
          const timeDiff = Math.abs(new Date(timestamp).getTime() - hoverTime);
          if (timeDiff < smallestTimeDiff) {
            smallestTimeDiff = timeDiff;
            closestPointIndex = i;
          }
        });
        
        // Draw vertical line
        if (closestPointIndex !== -1) {
          const x = padding.left + ((width - padding.left - padding.right) * closestPointIndex) / (prices.length - 1);
          
          // Draw vertical line
          ctx.beginPath();
          ctx.setLineDash([4, 2]);
          ctx.moveTo(x, padding.top);
          ctx.lineTo(x, height - padding.bottom);
          ctx.strokeStyle = isDarkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)';
          ctx.lineWidth = 1;
          ctx.stroke();
          ctx.setLineDash([]);
          
          // Draw point
          ctx.beginPath();
          ctx.arc(x, points[closestPointIndex].y, 4, 0, Math.PI * 2);
          ctx.fillStyle = isDarkMode ? '#fff' : '#000';
          ctx.fill();
          ctx.strokeStyle = lineColor;
          ctx.lineWidth = 2;
          ctx.stroke();
          
          // Update tooltip data
          const price = prices[closestPointIndex];
          setTooltipData({
            price,
            timestamp: timestamps[closestPointIndex],
            x,
            y: points[closestPointIndex].y
          });
        }
      } else if (!hoveredPoint && !isDragging) {
        // Clear tooltip if there's no hovered point
        setTooltipData(null);
      }
      
      // Draw drag selection area
      if (isDragging && dragStart && dragEnd) {
        // Find closest point indices
        let startIndex = 0;
        let endIndex = 0;
        let smallestStartDiff = Infinity;
        let smallestEndDiff = Infinity;
        
        timestamps.forEach((timestamp, i) => {
          const startDiff = Math.abs(new Date(timestamp).getTime() - new Date(dragStart.timestamp).getTime());
          const endDiff = Math.abs(new Date(timestamp).getTime() - new Date(dragEnd.timestamp).getTime());
          
          if (startDiff < smallestStartDiff) {
            smallestStartDiff = startDiff;
            startIndex = i;
          }
          
          if (endDiff < smallestEndDiff) {
            smallestEndDiff = endDiff;
            endIndex = i;
          }
        });
        
        // Draw selection area
        const startX = padding.left + ((width - padding.left - padding.right) * startIndex) / (prices.length - 1);
        const endX = padding.left + ((width - padding.left - padding.right) * endIndex) / (prices.length - 1);
        
        // Draw rectangle
        ctx.fillStyle = isDarkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.2)';
        ctx.fillRect(Math.min(startX, endX), padding.top, Math.abs(endX - startX), height - padding.top - padding.bottom);
        
        // Draw border lines
        ctx.beginPath();
        ctx.moveTo(startX, padding.top);
        ctx.lineTo(startX, height - padding.bottom);
        ctx.moveTo(endX, padding.top);
        ctx.lineTo(endX, height - padding.bottom);
        ctx.strokeStyle = isDarkMode ? 'rgba(59, 130, 246, 0.6)' : 'rgba(59, 130, 246, 0.6)';
        ctx.lineWidth = 1;
        ctx.stroke();
        
        // Draw start and end points
        ctx.beginPath();
        const startY = height - padding.bottom - ((prices[startIndex] - minPrice) / priceRange) * (height - padding.top - padding.bottom);
        const endY = height - padding.bottom - ((prices[endIndex] - minPrice) / priceRange) * (height - padding.top - padding.bottom);
        
        ctx.arc(startX, startY, 4, 0, Math.PI * 2);
        ctx.arc(endX, endY, 4, 0, Math.PI * 2);
        ctx.fillStyle = isDarkMode ? '#3b82f6' : '#3b82f6';
        ctx.fill();
      }
    }
  }, [chartData, expanded, priceData, expandedChart, isDarkMode, selectedTimeframe, hoveredPoint, isDragging, dragStart, dragEnd]);

  // Handle mouse events for hover/tooltip effects
  useEffect(() => {
    const chartElement = chartRef.current;
    const chartContainer = chartContainerRef.current;
    
    if (!chartElement || !chartContainer || !chartData || !expanded) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      if (!chartData.prices || chartData.prices.length === 0) return;
      
      const rect = chartElement.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      const width = chartElement.width;
      const height = chartElement.height;
      const padding = { left: 40, right: 50, top: 10, bottom: 20 };
      
      // Check if mouse is within chart area
      if (x < padding.left || x > width - padding.right || 
          y < padding.top || y > height - padding.bottom) {
        return;
      }
      
      // Calculate which data point is closest to the mouse
      const dataWidth = width - padding.left - padding.right;
      const pointIndex = Math.round(((x - padding.left) / dataWidth) * (chartData.prices.length - 1));
      
      if (pointIndex >= 0 && pointIndex < chartData.prices.length) {
        const timestamp = chartData.timestamps[pointIndex];
        
        if (isDragging) {
          if (!dragStart) {
            setDragStart({
              timestamp,
              x,
              price: chartData.prices[pointIndex]
            });
          } else {
            setDragEnd({
              timestamp,
              x,
              price: chartData.prices[pointIndex]
            });
          }
        } else {
          // Just hovering - update hovered point in context
          setHoveredPoint({
            timestamp,
            x
          });
        }
      }
    };
    
    const handleMouseOut = () => {
      if (!isDragging) {
        setHoveredPoint(null);
      }
    };
    
    const handleMouseDown = (e: MouseEvent) => {
      // Start dragging on mouse down if not already dragging
      if (!isDragging) {
        setIsDragging(true);
        setDragStart(null);
        setDragEnd(null);
        
        // Trigger handleMouseMove to set the initial drag point
        handleMouseMove(e);
      }
    };
    
    const handleMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
      }
    };
    
    // Add event listeners
    chartContainer.addEventListener('mousemove', handleMouseMove);
    chartContainer.addEventListener('mouseout', handleMouseOut);
    chartContainer.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);
    
    // Remove event listeners on cleanup
    return () => {
      chartContainer.removeEventListener('mousemove', handleMouseMove);
      chartContainer.removeEventListener('mouseout', handleMouseOut);
      chartContainer.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [chartData, expanded, isDragging, dragStart, dragEnd, setHoveredPoint, setIsDragging, setDragStart, setDragEnd]);

  // Update the expanded status in context
  useEffect(() => {
    toggleStockExpansion(ticker, expanded, expandedChart);
  }, [expanded, expandedChart, ticker, toggleStockExpansion]);

  const toggleExpanded = () => {
    setExpanded(!expanded);
    if (expandedChart && !expanded) {
      setExpandedChart(false);
    }
  };

  const toggleExpandedChart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedChart(!expandedChart);
  };

  const handleMinimize = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedChart(false);
    setExpanded(false);
  };

  const handleViewFullChart = (e: React.MouseEvent) => {
    e.stopPropagation();
    openStockModal(ticker);
  };
  
  const handleTimeframeChange = (timeframe: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    // Update timeframe in the shared context so all charts update
    setSelectedTimeframe(timeframe);
  };

  // Format price with commas and 2 decimal places
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);
  };

  // Format percent change
  const formatPercentChange = (change: number) => {
    return (change > 0 ? '+' : '') + change.toFixed(2) + '%';
  };

  // Format large numbers (volume, market cap)
  const formatLargeNumber = (num: number) => {
    if (num >= 1e12) return (num / 1e12).toFixed(2) + 'T';
    if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(2) + 'K';
    return num.toString();
  };

  // Format date based on timeframe
  const formatChartDate = (timestamp: string) => {
    const date = new Date(timestamp);
    
    if (['1d', '5d'].includes(selectedTimeframe)) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (['1mo', '3mo'].includes(selectedTimeframe)) {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: '2-digit' });
    }
  };
  
  // Format company name for display (truncate if too long)
  const getDisplayName = () => {
    const name = companyName || fetchedCompanyName;
    if (!name) return null;
    
    const maxLength = expanded ? 40 : 30;
    return name.length > maxLength ? name.substring(0, maxLength) + '...' : name;
  };

  // Calculate price change for selection
  const calculateSelectionChange = () => {
    if (!dragStart || !dragEnd || !chartData || !chartData.prices || chartData.prices.length === 0) {
      return null;
    }
    
    // Find closest points
    let startIndex = 0;
    let endIndex = 0;
    let smallestStartDiff = Infinity;
    let smallestEndDiff = Infinity;
    
    chartData.timestamps.forEach((timestamp, i) => {
      const startDiff = Math.abs(new Date(timestamp).getTime() - new Date(dragStart.timestamp).getTime());
      const endDiff = Math.abs(new Date(timestamp).getTime() - new Date(dragEnd.timestamp).getTime());
      
      if (startDiff < smallestStartDiff) {
        smallestStartDiff = startDiff;
        startIndex = i;
      }
      
      if (endDiff < smallestEndDiff) {
        smallestEndDiff = endDiff;
        endIndex = i;
      }
    });
    
    const startPrice = chartData.prices[startIndex];
    const endPrice = chartData.prices[endIndex];
    const change = endPrice - startPrice;
    const percentChange = (change / startPrice) * 100;
    
    return {
      startPrice,
      endPrice,
      change,
      percentChange,
      startTime: chartData.timestamps[startIndex],
      endTime: chartData.timestamps[endIndex]
    };
  };

  // Render nothing for invalid stocks
  if (isValidStock === false) {
    return null;
  }

  // Don't show loading placeholder - just return null until we know if it's valid
  if (isLoading && isValidStock === null) {
    return null;
  }
  
  // Determine colors based on price change direction
  const getPriceChangeColors = () => {
    if (!priceData) return { 
      textColor: 'text-gray-400', 
      bgColor: 'bg-gray-700/30', 
      borderColor: 'border-gray-600/50',
      fillColor: 'rgba(107, 114, 128, 0.2)',
      strokeColor: isDarkMode ? '#9ca3af' : '#6b7280'
    };
    
    return priceData.change >= 0 
      ? { 
          textColor: isDarkMode ? 'text-emerald-300' : 'text-emerald-600',
          bgColor: 'bg-emerald-500/10', 
          borderColor: 'border-emerald-500/20',
          fillColor: isDarkMode ? 'rgba(16, 185, 129, 0.2)' : 'rgba(5, 150, 105, 0.1)',
          strokeColor: isDarkMode ? '#10b981' : '#059669'
        }
      : {
          textColor: isDarkMode ? 'text-rose-300' : 'text-rose-600',
          bgColor: 'bg-rose-500/10',
          borderColor: 'border-rose-500/20',
          fillColor: isDarkMode ? 'rgba(244, 63, 94, 0.2)' : 'rgba(225, 29, 72, 0.1)',
          strokeColor: isDarkMode ? '#f43f5e' : '#e11d48'
        };
  };
  
  const { textColor, bgColor, borderColor, fillColor, strokeColor } = getPriceChangeColors();

  // Determine positioning styles based on expanded state and position
  const getPositionStyles = () => {
    if (!expanded || !position) {
      return {
        gridArea: 'auto',
        marginRight: !expanded ? '0.5rem' : '0',
        marginBottom: '0.5rem',
        width: 'auto',
      };
    }

    const expandedCount = expandedStocks.size;
    const isComparison = expandedCount > 1;
    
    return {
      gridArea: `${position.row + 1} / ${position.column + 1} / auto / auto`,
      marginRight: '0',
      marginBottom: '0.5rem',
      // When comparing, make them equal width
      width: isComparison ? (expandedCount <= 2 ? '49%' : position.column === 0 ? '49%' : '49%') : '350px',
    };
  };
  
  const positionStyles = getPositionStyles();
  
  // Determine if this is in comparative mode (side by side with other stock)
  const isInComparisonMode = expanded && expandedStocks.size > 1;
  const selectionStats = calculateSelectionChange();

  return (
    <motion.div
      className="relative z-10"
      layout
      transition={{ 
        layout: { 
          type: "spring", 
          stiffness: 350, 
          damping: 25 
        }
      }}
      style={{
        marginRight: positionStyles.marginRight,
        marginBottom: positionStyles.marginBottom,
        width: expanded ? positionStyles.width : 'auto',
        gridArea: positionStyles.gridArea,
      }}
    >
      <motion.div
        className={`relative overflow-hidden rounded-xl ${
          expanded 
            ? expandedChart 
              ? 'w-full max-w-2xl'
              : isInComparisonMode ? 'w-full' : 'w-[380px]'
            : 'inline-block'
        } ${
          isDarkMode 
            ? 'bg-gray-800/90 border border-gray-700/80'
            : 'bg-white/95 border border-gray-200/90'
        } transition-all duration-200 ${expanded ? 'shadow-md' : 'shadow-sm'}`}
        initial={false}
        animate={expanded ? { 
          height: expandedChart ? 'auto' : 'auto',
          borderRadius: '0.75rem'
        } : { 
          height: 'auto',
          borderRadius: '0.5rem'
        }}
        onClick={toggleExpanded}
        whileHover={expanded ? {} : { scale: 1.02, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}
        whileTap={expanded ? {} : { scale: 0.98 }}
        style={{
          cursor: 'pointer',
          backdropFilter: 'blur(8px)'
        }}
      >
        {/* Stock Header - Always visible */}
        <div className={`flex items-center justify-between p-3 ${
          expanded ? 'border-b' : ''
        } ${
          isDarkMode 
            ? 'border-gray-700/70 text-white' 
            : 'border-gray-200/70 text-gray-800'
        }`}>
          <div className="flex items-center space-x-3 min-w-0">
            {/* Logo */}
            <div className={`overflow-hidden w-10 h-10 flex-shrink-0 rounded-lg flex items-center justify-center 
              ${isDarkMode ? 'bg-gray-700/70' : 'bg-gray-50'} 
              border ${isDarkMode ? 'border-gray-600/70' : 'border-gray-200/70'} p-0.5`}
            >
              {!imageError ? (
                <img
                  src={`/api/market/company-logo/${ticker}`}
                  alt={`${ticker} logo`}
                  className="w-full h-full object-cover rounded"
                  onError={() => setImageError(true)}
                />
              ) : (
                <span className={`text-lg font-semibold ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  {ticker[0]}
                </span>
              )}
            </div>
            
            {/* Ticker and Name */}
            <div className="flex flex-col min-w-0 flex-1">
              <div className="flex items-center flex-wrap gap-1.5">
                <span className="font-medium text-base whitespace-nowrap">{ticker}</span>
                {prefix && (
                  <span className={`text-xs px-1.5 rounded-full font-medium uppercase tracking-wide whitespace-nowrap ${
                    (() => {
                      // Color coding based on prefix type
                      if (prefix.includes('Primary')) return isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700';
                      if (prefix.includes('Related')) return isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700';
                      if (prefix.includes('Comparison')) return isDarkMode ? 'bg-amber-900/50 text-amber-300' : 'bg-amber-100 text-amber-700';
                      if (prefix.includes('Sector')) return isDarkMode ? 'bg-teal-900/50 text-teal-300' : 'bg-teal-100 text-teal-700';
                      if (prefix.includes('Index')) return isDarkMode ? 'bg-gray-900/50 text-gray-300' : 'bg-gray-200 text-gray-700';
                      return isDarkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700';
                    })()
                  }`}>
                    {prefix.replace(/[\[\]]/g, '')}
                  </span>
                )}
                
                {isInComparisonMode && expanded && (
                  <span className={`text-2xs px-1.5 py-0.5 rounded-full font-medium uppercase tracking-wide whitespace-nowrap ${
                    isDarkMode ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-100 text-indigo-700'
                  }`}>
                    Comparison
                  </span>
                )}
              </div>
              {getDisplayName() && (
                <div className="text-sm truncate max-w-[260px] mt-0.5 opacity-80">
                  {getDisplayName()}
                </div>
              )}
            </div>
          </div>
          
          {/* Price Change and Controls */}
          <div className="flex items-center space-x-2 flex-shrink-0">
            {priceData && (
              <div className="flex flex-col items-end">
                <span className={`text-base font-semibold whitespace-nowrap ${textColor}`}>
                  ${formatPrice(priceData.price)}
                </span>
                <div className="flex items-center mt-0.5">
                  <span className={`text-xs font-medium rounded-full px-2 py-0.5 flex items-center whitespace-nowrap ${
                    priceData.change >= 0
                      ? isDarkMode ? 'bg-emerald-900/30 text-emerald-300' : 'bg-emerald-100 text-emerald-700'
                      : isDarkMode ? 'bg-rose-900/30 text-rose-300' : 'bg-rose-100 text-rose-700'
                  }`}>
                    {priceData.change >= 0 ? 
                      <ArrowUp size={12} className="mr-0.5" /> : 
                      <ArrowDown size={12} className="mr-0.5" />
                    }
                    {formatPercentChange(priceData.changePercent)}
                  </span>
                </div>
              </div>
            )}
            
            {expanded ? (
              <div className="flex items-center space-x-1 ml-1">
                <button 
                  onClick={(e) => toggleExpandedChart(e)}
                  className={`p-1.5 rounded-md ${
                    isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
                  } transition-colors`}
                  title={expandedChart ? "Collapse chart" : "Expand chart"}
                >
                  {expandedChart ? (
                    <Minimize2 size={16} />
                  ) : (
                    <Maximize2 size={16} />
                  )}
                </button>
                <button
                  onClick={(e) => handleMinimize(e)}
                  className={`p-1.5 rounded-md ${
                    isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
                  } transition-colors`}
                  title="Minimize"
                >
                  <ChevronUp size={16} />
                </button>
              </div>
            ) : (
              <ChevronDown size={18} className="ml-1 transform transition-transform duration-200 text-gray-400" />
            )}
          </div>
        </div>
        
        {/* Expanded Content */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              {/* Loading state */}
              {isLoading && !priceData && (
                <div className={`p-6 flex justify-center ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  <div className="w-7 h-7 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
              
              {/* Error state */}
              {loadError && (
                <div className={`p-4 text-center ${
                  isDarkMode ? 'text-rose-400' : 'text-rose-600'
                }`}>
                  <AlertCircle size={16} className="inline mr-2" />
                  {loadError}
                </div>
              )}
              
              {/* Stock data */}
              {priceData && (
                <>
                  {/* Price info */}
                  <div className={`px-4 pt-4 pb-3 ${expandedChart ? 'border-b' : ''} ${
                    isDarkMode ? 'border-gray-700/70' : 'border-gray-200/70'
                  }`}>
                    <div className="flex justify-between items-baseline">
                      <div>
                        <span className={`text-2xl font-bold ${
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                          ${formatPrice(priceData.price)}
                        </span>
                        <div className="flex items-center space-x-2 mt-1">
                          <div className={`flex items-center ${textColor}`}>
                            {priceData.change >= 0 ? (
                              <ArrowUp size={14} className="mr-1" />
                            ) : (
                              <ArrowDown size={14} className="mr-1" />
                            )}
                            <span className="text-sm font-medium">${formatPrice(Math.abs(priceData.change))}</span>
                          </div>
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                            priceData.change >= 0
                              ? isDarkMode ? 'bg-emerald-900/30 text-emerald-300' : 'bg-emerald-100 text-emerald-700'
                              : isDarkMode ? 'bg-rose-900/30 text-rose-300' : 'bg-rose-100 text-rose-700'
                          }`}>
                            {formatPercentChange(priceData.changePercent)}
                          </span>
                        </div>
                      </div>
                      
                      {priceData.timestamp && (
                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Last updated: {new Date(priceData.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Timeframe selector */}
                  <div className={`flex items-center justify-between px-4 py-2.5 ${
                    isDarkMode ? 'bg-gray-750/50' : 'bg-gray-50/40'
                  }`}>
                    <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
                      {timeframes.map(tf => (
                        <button
                          key={tf.value}
                          onClick={handleTimeframeChange(tf.value)}
                          className={`px-2.5 py-1 text-xs rounded-full transition-colors ${
                            selectedTimeframe === tf.value
                              ? isDarkMode 
                                ? 'bg-blue-600/30 text-blue-300 font-medium' 
                                : 'bg-blue-100 text-blue-700 font-medium'
                              : isDarkMode
                                ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700/40'
                                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200/60'
                          }`}
                        >
                          {tf.label}
                        </button>
                      ))}
                    </div>
                    
                    {isLoading && selectedTimeframe !== '1d' && (
                      <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    )}
                  </div>
                  
                  {/* Chart container */}
                  <div 
                    ref={chartContainerRef}
                    className={`w-full relative ${
                      expandedChart ? 'h-72' : 'h-40'
                    } ${isDarkMode ? 'bg-gray-850/40' : 'bg-white/40'} p-4`}
                  >
                    {(!chartData || isLoading) && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" 
                             style={{ 
                               borderColor: strokeColor,
                               borderTopColor: 'transparent' 
                             }} 
                        />
                      </div>
                    )}
                    
                    <canvas 
                      ref={chartRef}
                      className="w-full h-full"
                      width={expandedChart ? 700 : 350}
                      height={expandedChart ? 250 : 120}
                    />
                    
                    {/* Tooltip overlay */}
                    {tooltipData && expanded && (
                      <div
                        className={`absolute px-3 py-2 rounded-lg shadow-lg pointer-events-none backdrop-blur-sm ${
                          isDarkMode ? 'bg-gray-800/95 text-gray-200' : 'bg-white/95 text-gray-800'
                        } border ${
                          isDarkMode ? 'border-gray-700' : 'border-gray-300'
                        }`}
                        style={{
                          left: tooltipData.x + (chartRef.current && tooltipData.x > (chartRef.current.width / 2) ? -120 : 10),
                          top: chartRef.current ? Math.min(tooltipData.y - 50, chartRef.current.height - 90) : tooltipData.y - 50,
                          zIndex: 50
                        }}
                      >
                        <div className="text-xs font-semibold">${formatPrice(tooltipData.price)}</div>
                        <div className="text-xs opacity-80">{formatChartDate(tooltipData.timestamp)}</div>
                      </div>
                    )}
                    
                    {/* Selection stats overlay */}
                    {isDragging && selectionStats && (
                      <div
                        className={`absolute right-2 top-2 px-4 py-2 rounded-lg shadow-lg backdrop-blur-sm ${
                          isDarkMode ? 'bg-gray-800/95 text-gray-200' : 'bg-white/95 text-gray-800'
                        } border ${
                          isDarkMode ? 'border-gray-700' : 'border-gray-300'
                        }`}
                      >
                        <div className="text-sm font-semibold flex items-center space-x-2">
                          <span className={selectionStats.change >= 0 ? 
                            (isDarkMode ? 'text-emerald-400' : 'text-emerald-600') : 
                            (isDarkMode ? 'text-rose-400' : 'text-rose-600')
                          }>
                            {selectionStats.change >= 0 ? '+' : ''}${formatPrice(selectionStats.change)} 
                            ({selectionStats.change >= 0 ? '+' : ''}{selectionStats.percentChange.toFixed(2)}%)
                          </span>
                        </div>
                        <div className="text-xs opacity-80 mt-1">
                          {formatChartDate(selectionStats.startTime)} - {formatChartDate(selectionStats.endTime)}
                        </div>
                      </div>
                    )}
                    
                    {/* Chart controls */}
                    <div className="absolute bottom-2 right-2">
                      <button
                        onClick={handleViewFullChart}
                        className={`px-2.5 py-1 text-xs rounded-md ${
                          isDarkMode 
                            ? 'bg-gray-700/80 hover:bg-gray-700 text-gray-300' 
                            : 'bg-gray-200/80 hover:bg-gray-200 text-gray-700'
                        } flex items-center shadow-sm backdrop-blur-sm`}
                      >
                        <LineChart size={12} className="mr-1" />
                        Full Chart
                      </button>
                    </div>
                    
                    {/* Chart instructions */}
                    {expanded && (
                      <div className="absolute bottom-2 left-2">
                        <div className={`text-xs opacity-50 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          Hover to compare • Click & drag to measure
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Additional stats */}
                  <div className={`grid grid-cols-2 gap-x-6 gap-y-2.5 p-4 text-sm ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    <div className="flex justify-between">
                      <span className="text-opacity-70">Open</span>
                      <span className="font-medium">${formatPrice(priceData.open)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-opacity-70">Prev Close</span>
                      <span className="font-medium">${formatPrice(priceData.prevClose)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-opacity-70">Day High</span>
                      <span className="font-medium">${formatPrice(priceData.high)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-opacity-70">Day Low</span>
                      <span className="font-medium">${formatPrice(priceData.low)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-opacity-70">Volume</span>
                      <span className="font-medium">{formatLargeNumber(priceData.volume)}</span>
                    </div>
                    {priceData.marketCap && (
                      <div className="flex justify-between">
                        <span className="text-opacity-70">Market Cap</span>
                        <span className="font-medium">${formatLargeNumber(priceData.marketCap)}</span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

const InteractiveStockChip: React.FC<{
  ticker: string;
  companyName?: string;
  prefix: string;
  isDarkMode: boolean;
  readOnly?: boolean;
}> = ({ ticker, companyName, prefix, isDarkMode, readOnly = false }) => {
  const [imageError, setImageError] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { openStockModal } = useStockView();
  const { saveItem, watchlists, createList, addToList } = useWatchlists();
  const [showWatchlistMenu, setShowWatchlistMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [newListName, setNewListName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();

  // Handle clicks outside the menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowWatchlistMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSaveStock = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!user) {
      window.location.href = '/signup';
      return;
    }
    
    setShowWatchlistMenu(true);
  };

  const handleSaveIndividual = async () => {
    setIsLoading(true);
    try {
      await saveItem({
        type: 'STOCK',
        identifier: ticker,
        title: companyName || ticker
      });
      setShowWatchlistMenu(false);
    } catch (error) {
      console.error('Failed to save stock:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateList = async () => {
    if (!newListName.trim()) return;
    
    setIsLoading(true);
    try {
      const newList = await createList({ name: newListName });
      if (newList?.id) {
        await addToList(ticker, newList.id);
      }
      setShowWatchlistMenu(false);
      setShowCreateForm(false);
      setNewListName('');
    } catch (error) {
      console.error('Failed to create list:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddToList = async (listId: string) => {
    setIsLoading(true);
    try {
      await addToList(ticker, listId);
      setShowWatchlistMenu(false);
    } catch (error) {
      console.error('Failed to add to list:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewChart = (e: React.MouseEvent) => {
    e.stopPropagation();
    openStockModal(ticker);
  };

  const handleAddToChat = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(ticker);
  };

  return (
    <motion.div
      className="inline-flex items-center gap-1 py-1 px-2 rounded-md text-sm relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      initial={false}
      animate={{
        backgroundColor: isDarkMode ? 'rgba(22, 163, 74, 0.1)' : 'rgba(240, 253, 244, 1)',
        color: isDarkMode ? 'rgb(134, 239, 172)' : 'rgb(22, 101, 52)',
        borderColor: isDarkMode ? 'rgba(21, 128, 61, 0.3)' : 'rgba(187, 247, 208, 1)'
      }}
      style={{
        borderWidth: '1px',
        borderStyle: 'solid'
      }}
      transition={{
        type: "spring",
        stiffness: 700,  // Increased stiffness for faster animation
        damping: 25,
        duration: 0.1    // Shorter duration
      }}
    >
      {/* Company logo or fallback */}
      <div className={`overflow-hidden w-5 h-5 rounded-full flex items-center justify-center 
        ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'} 
        border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} mr-1`}
      >
        {!imageError ? (
          <img
            src={`/api/market/company-logo/${ticker}`}
            alt={`${ticker} logo`}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <span className={`text-xs font-semibold ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            {ticker[0]}
          </span>
        )}
      </div>
      
      {prefix && (
        <span className={`text-xs px-1.5 py-0.5 rounded ${
          isDarkMode ? 'bg-green-800/70 text-green-300' : 'bg-green-200 text-green-700'
        }`}>
          {prefix.replace(/[\[\]]/g, '')}
        </span>
      )}
      
      <span className=" font-medium">{ticker}</span>
      {companyName && <span className="text-xs break-normal">{companyName}</span>}
      
      {/* Action buttons that slide in when hovered */}
      <AnimatePresence>
        {isHovered && !readOnly && (
          <motion.div
            className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center"
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 'auto' }}
            exit={{ opacity: 0, width: 0 }}
            transition={{ 
              type: "spring",
              stiffness: 800,
              damping: 20,
              duration: 0.08 // Much quicker animation
            }}
          >
            <motion.div 
              className="flex space-x-1.5"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{
                type: "spring",
                stiffness: 800,
                damping: 20,
                staggerChildren: 0.02, // Faster stagger effect
                delayChildren: 0.01  // Faster delay
              }}
            >
              {/* Stock action buttons */}
              <motion.button
                onClick={handleViewChart}
                className={`w-4 h-4 rounded-full flex items-center justify-center transition-colors ${
                  isDarkMode ? 'text-green-300 hover:text-white' : 'text-green-700 hover:text-green-900'
                }`}
                whileHover={{ scale: 1.15 }}
                whileTap={{ scale: 0.9 }}
                title="View Chart"
              >
                <LineChart className="w-3.5 h-3.5" />
              </motion.button>
              
              <motion.button
                onClick={handleAddToChat}
                className={`w-4 h-4 rounded-full flex items-center justify-center transition-colors ${
                  isDarkMode ? 'text-green-300 hover:text-white' : 'text-green-700 hover:text-green-900'
                }`}
                whileHover={{ scale: 1.15 }}
                whileTap={{ scale: 0.9 }}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 800, damping: 20, delay: 0.02 }}
                title="Add to Chat"
              >
                <Box className="w-3.5 h-3.5" />
              </motion.button>
              
              <motion.button
                onClick={handleSaveStock}
                className={`w-4 h-4 rounded-full flex items-center justify-center transition-colors ${
                  isDarkMode ? 'text-green-300 hover:text-white' : 'text-green-700 hover:text-green-900'
                }`}
                whileHover={{ scale: 1.15 }}
                whileTap={{ scale: 0.9 }}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 800, damping: 20, delay: 0.04 }}
                title="Add to Watchlist"
              >
                <BookmarkPlus className="w-3.5 h-3.5" />
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Watchlist selection menu */}
      <AnimatePresence>
        {showWatchlistMenu && (
          <motion.div 
            ref={menuRef}
            className="absolute top-full right-0 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 min-w-[200px] overflow-hidden"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{
              type: "spring",
              stiffness: 500,
              damping: 30
            }}
          >
            {showCreateForm ? (
              <div className="p-3 space-y-3">
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  Create new list
                </div>
                
                <input
                  type="text"
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  placeholder="List name"
                  className={`w-full px-3 py-2 rounded-lg border ${
                    isDarkMode 
                      ? 'bg-gray-800 border-gray-700 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  } focus:ring-2 focus:ring-blue-500`}
                />
                <div className="flex space-x-2">
                  <button
                    onClick={() => setShowCreateForm(false)}
                    className={`flex-1 py-2 rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateList}
                    disabled={!newListName.trim() || isLoading}
                    className="flex-1 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600
                      disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Creating...' : 'Create'}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <button
                  onClick={handleSaveIndividual}
                  disabled={isLoading}
                  className={`w-full flex items-center justify-between p-3 rounded-lg
                    ${isDarkMode 
                      ? 'hover:bg-gray-800 text-gray-300' 
                      : 'hover:bg-gray-100 text-gray-700'}`}
                >
                  <div className="flex items-center space-x-3">
                    <BookmarkPlus className="w-4 h-4 text-blue-500" />
                    <span>Save individually</span>
                  </div>
                  
                  {isLoading && (
                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  )}
                </button>
                
                <div className={`py-1 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} border-t`}>
                  <div className="px-3 py-2 text-xs text-gray-500">Add to list</div>
                  
                  {watchlists.length === 0 ? (
                    <div className="p-3 text-sm text-gray-500">No lists yet</div>
                  ) : (
                    watchlists.map(list => (
                      <button
                        key={list.id}
                        onClick={() => handleAddToList(list.id)}
                        disabled={isLoading}
                        className={`w-full flex items-center justify-between p-3 rounded-lg
                          ${isDarkMode 
                            ? 'hover:bg-gray-800 text-gray-300' 
                            : 'hover:bg-gray-100 text-gray-700'}`}
                      >
                        <div className="flex items-center space-x-3">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" 
                             stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" 
                             className="text-gray-400">
                            <line x1="8" y1="6" x2="21" y2="6"></line>
                            <line x1="8" y1="12" x2="21" y2="12"></line>
                            <line x1="8" y1="18" x2="21" y2="18"></line>
                            <line x1="3" y1="6" x2="3.01" y2="6"></line>
                            <line x1="3" y1="12" x2="3.01" y2="12"></line>
                            <line x1="3" y1="18" x2="3.01" y2="18"></line>
                          </svg>
                          <span>{list.name}</span>
                        </div>
                        
                        {isLoading && (
                          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        )}
                      </button>
                    ))
                  )}
                </div>
                
                <button
                  onClick={() => setShowCreateForm(true)}
                  className={`w-full flex items-center py-3 px-3 ${
                    isDarkMode ? 'border-t border-gray-700 hover:bg-gray-800' : 'border-t border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <Plus className="w-4 h-4 mr-2 text-blue-500" />
                  <span className="text-blue-500 text-sm">Create new list</span>
                </button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const SourceIcon: React.FC<{ type: string, source: string }> = ({ type, source }) => {
  const { isDarkMode } = useTheme();
  const [imageError, setImageError] = useState(false);
  
  // Get favicon for web sources
  const getFaviconUrl = (source: string): string => {
    try {
      const hostname = new URL(source).hostname;
      return `https://www.google.com/s2/favicons?domain=${hostname}&sz=64`;
    } catch {
      // For non-URL sources, try to extract domain name
      const domainMatch = source.match(/^(?:https?:\/\/)?(?:[^@\n]+@)?(?:www\.)?([^:/\n]+)/im);
      const domain = domainMatch ? domainMatch[1] : source;
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
    }
  };
  
  // Non-web sources or fallback for image errors
  if (imageError || type !== 'WEB') {
    let bgColor = 'bg-blue-500/20';
    let textColor = 'text-blue-400';
    
    switch (type) {
      case 'NEWS':
        bgColor = 'bg-yellow-500/20';
        textColor = 'text-yellow-400';
        break;
      case 'MARKET_DATA':
        bgColor = 'bg-green-500/20';
        textColor = 'text-green-400';
        break;
      case 'TECHNICAL':
        bgColor = 'bg-purple-500/20';
        textColor = 'text-purple-400';
        break;
      case 'FUNDAMENTAL':
        bgColor = 'bg-orange-500/20';
        textColor = 'text-orange-400';
        break;
    }
    
    return (
      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${bgColor} border ${
        isDarkMode ? 'border-gray-700' : 'border-gray-200'
      }`}>
        <span className={`text-xs font-medium ${textColor}`}>
          {source[0].toUpperCase()}
        </span>
      </div>
    );
  }
  
  // Image-based favicon
  return (
    <div className={`w-6 h-6 rounded-full overflow-hidden flex items-center justify-center ${
      isDarkMode ? 'bg-gray-800' : 'bg-gray-100'
    } border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
      <img
        src={getFaviconUrl(source)}
        alt={source}
        className="w-6 h-6 object-contain"
        onError={() => setImageError(true)}
      />
    </div>
  );
};

const NewsSourcesIndicator: React.FC<NewsSourcesIndicatorProps> = ({ sources }) => {
  const { isDarkMode } = useTheme();
  const [showDetails, setShowDetails] = useState(false);
  const uniqueSources = Array.from(new Set(sources.map(s => s.source)));

  return (
    <div className="relative flex items-center mt-4">
      <div className={`h-5 w-px mr-3 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`} />
      <div className="flex items-center space-x-2">
        <div className="flex -space-x-2">
          {uniqueSources.slice(0, 3).map((source, i) => (
            <SourceIcon key={source} type="NEWS" source={source} />
          ))}
          {uniqueSources.length > 3 && (
            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
              isDarkMode ? 'bg-gray-800' : 'bg-gray-100'
            } border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <span className="text-xs font-medium text-gray-400">
                +{uniqueSources.length - 3}
              </span>
            </div>
          )}
        </div>
        <button
          onClick={() => setShowDetails(!showDetails)}
          className={`flex items-center space-x-1 text-sm ${
            isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-700'
          } transition-colors duration-200`}
        >
          <span>News Sources</span>
          <ChevronRight className={`w-4 h-4 transform transition-transform duration-200 ${showDetails ? 'rotate-90' : ''}`} />
        </button>
      </div>
      
      {showDetails && (
        <div className={`absolute left-56 top-0 w-96 p-4 rounded-lg shadow-lg z-50 ${
          isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
        }`}>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {sources.map((article, i) => (
              <div key={i} className={`p-3 rounded-lg ${
                isDarkMode ? 'bg-gray-750 hover:bg-gray-700' : 'bg-gray-50 hover:bg-gray-100'
              } transition-colors`}>
                <a 
                  href={article.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={`text-sm font-medium ${
                    isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
                  } flex items-start`}
                >
                  <span className="flex-1">{article.title}</span>
                  <ExternalLink className="w-3.5 h-3.5 ml-1 mt-0.5 flex-shrink-0" />
                </a>
                <p className={`text-xs mt-1 line-clamp-2 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {article.snippet}
                </p>
                <div className="flex items-center justify-between mt-2">
                  <span className={`text-xs ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-500'
                  }`}>
                    {article.source}
                  </span>
                  <span className={`text-xs ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-500'
                  }`}>
                    {new Date(article.date).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const WebSearchResultsIndicator: React.FC<WebSearchResultsProps> = ({ results }) => {
  const { isDarkMode } = useTheme();
  const [showDetails, setShowDetails] = useState(false);
  const uniqueSources = Array.from(new Set(results.map(s => s.source)));

  return (
    <div className="relative">
      {/* Main indicator row - entire area clickable */}
      <button 
        onClick={() => setShowDetails(!showDetails)}
        className={`flex items-center justify-between py-2 px-3 rounded-lg transition-colors ${
          isDarkMode 
            ? 'hover:bg-gray-800/60' 
            : 'hover:bg-gray-100/60'
        }`}
        aria-expanded={showDetails}
        aria-controls="web-sources-dropdown"
        style={{ width: 'auto' }} /* Reduce overall width */
      >
        <div className="flex items-center space-x-2">
          {/* White globe icon with no outline, bigger size */}
          <div className={`w-6 h-6 rounded-full flex items-center justify-center 
          }`}>
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="currentColor"
              className="text-white"
            >
              <path d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zM4 12c0-.899.156-1.762.431-2.569L6 11l2 2v2l2 2 1 1v1.931C7.061 19.436 4 16.072 4 12zm14.33 4.873C17.677 16.347 17 14.973 17 13.5v-2a.5.5 0 0 0-.5-.5h-2a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3.97a7.937 7.937 0 0 1 .03 3.373zm-1.009-8.42A7.93 7.93 0 0 1 18.931 11H17.93v-1a.5.5 0 0 0-.5-.5H16v-1h1.3a7.985 7.985 0 0 1 1.021-1.047z" />
            </svg>
          </div>
          {/* Larger, bolder text */}
          <span className={`text-base font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Web Sources
          </span>
        </div>

        {/* Favicon circles and chevron - moved closer */}
        <div className="flex items-center ml-12"> {/* Added ml-4 to bring closer */}
          <div className="flex -space-x-3">
            {uniqueSources.slice(0, 3).map((source, i) => (
              <SourceIcon key={`web-${i}`} type="WEB" source={source} />
            ))}
            {uniqueSources.length > 3 && (
              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                isDarkMode ? 'bg-gray-800' : 'bg-gray-100'
              } border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <span className="text-xs font-medium text-gray-400">
                  +{uniqueSources.length - 3}
                </span>
              </div>
            )}
          </div>
          <ChevronRight 
            className={`w-4 h-4 transform transition-transform duration-200 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            } ${showDetails ? 'rotate-90' : ''}`} 
          />
        </div>
      </button>
      
      {/* Integrated dropdown */}
      <AnimatePresence>
        {showDetails && (
          <motion.div
            id="web-sources-dropdown"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`overflow-hidden rounded-lg mb-4 ${
              isDarkMode ? 'bg-gray-800/70 border border-gray-700' : 'bg-gray-50/80 border border-gray-200'
            }`}
          >
            <div className="p-3 space-y-4 max-h-96 overflow-y-auto">
              {results.map((result, i) => (
                <div key={i} className={`p-3 rounded-lg transition-colors ${
                  isDarkMode ? 'bg-gray-750 hover:bg-gray-700' : 'bg-white hover:bg-gray-100'
                } border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} shadow-sm`}>
                  <a 
                    href={result.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`text-sm font-medium ${
                      isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
                    } flex items-start`}
                  >
                    <span className="flex-1">{result.title}</span>
                    <ExternalLink className="w-3.5 h-3.5 ml-1 mt-0.5 flex-shrink-0" />
                  </a>
                  <p className={`text-sm mt-2 line-clamp-2 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {result.snippet}
                  </p>
                  <div className="flex items-center justify-between mt-3">
                    <span className={`text-xs ${
                      isDarkMode ? 'text-gray-500' : 'text-gray-500'
                    }`}>
                      {result.source}
                    </span>
                    {result.date && (
                      <span className={`text-xs ${
                        isDarkMode ? 'text-gray-500' : 'text-gray-500'
                      }`}>
                        {new Date(result.date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ResearchSourcesIndicator: React.FC<ResearchSourcesProps> = ({ sources, searchQueries = [] }) => {
  const { isDarkMode } = useTheme();
  const [showDetails, setShowDetails] = useState(false);
  
  // Group sources by type for the dropdown
  const sourcesByType = sources.reduce((acc, source) => {
    const type = source.type || 'UNKNOWN';
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(source);
    return acc;
  }, {} as Record<string, ResearchSource[]>);
  
  // Count sources by type
  const typeCounts = Object.entries(sourcesByType).map(([type, srcs]) => ({
    type,
    count: srcs.length
  }));

  // Extract search queries from sources if not provided explicitly
  const extractedQueries = useMemo(() => {
    if (searchQueries && searchQueries.length > 0) return searchQueries;
    
    // Try to extract search queries from the sources
    const queries = new Set<string>();
    const queryObjects: { query: string; timestamp: string; resultsCount: number }[] = [];
    
    sources.forEach(source => {
      if (source.metadata?.query && !queries.has(source.metadata.query)) {
        queries.add(source.metadata.query);
        queryObjects.push({
          query: source.metadata.query,
          timestamp: source.timestamp || new Date().toISOString(),
          resultsCount: 1 // Default to 1 if we don't have actual count
        });
      }
    });
    
    return queryObjects;
  }, [sources, searchQueries]);

  return (
    <div className="relative mt-1">
      {/* Main indicator row - entire area clickable */}
      <button 
        onClick={() => setShowDetails(!showDetails)}
        className={`flex items-center justify-between mb-2 py-2 px-3 rounded-lg transition-colors ${
          isDarkMode 
            ? 'hover:bg-gray-800/60' 
            : 'hover:bg-gray-100/60'
        }`}
        aria-expanded={showDetails}
        aria-controls="research-methods-dropdown"
        style={{ width: 'auto' }}
      >
        <div className="flex items-center space-x-2">
          {/* Search icon */}
          <div className={`w-6 h-6 flex items-center justify-center 
            
          }`}>
            <div className={`w-6 h-6 rounded-full flex flex-col items-center justify-center`}>
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="20" 
              height="20" 
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor" 
              strokeWidth="2"
              strokeLinecap="square"
              strokeLinejoin="bevel"
              className="text-white"
            >
              <line x1="6" y1="6" x2="18" y2="6"></line>
              <line x1="6" y1="12" x2="18" y2="12"></line>
              <line x1="6" y1="18" x2="18" y2="18"></line>
            </svg>
          </div>
          </div>
          {/* Larger, bolder text */}
          <span className={`text-base font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Research Methods
          </span>
        </div>

        {/* Query count and chevron */}
        <div className="flex items-center ml-12">
          <div className={`px-2 py-0.5 rounded-full text-xs ${
            isDarkMode ? 'bg-gray-500/20 text-white' : 'bg-gray-100 text-white'
          }`}>
            {extractedQueries.length} {extractedQueries.length === 1 ? 'query' : 'queries'}
          </div>
          <ChevronRight 
            className={`w-4 h-4 transform transition-transform duration-200 ml-2 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            } ${showDetails ? 'rotate-90' : ''}`} 
          />
        </div>
      </button>
      
      {/* Integrated dropdown */}
      <AnimatePresence>
        {showDetails && (
          <motion.div
            id="research-methods-dropdown"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`overflow-hidden rounded-lg mb-4 ${
              isDarkMode ? 'bg-gray-800/70 border border-gray-700' : 'bg-gray-50/80 border border-gray-200'
            }`}
          >
            <div className="p-3 space-y-4 max-h-96 overflow-y-auto">
              {extractedQueries.length > 0 ? (
                <div>
                  <h3 className={`text-sm font-medium mb-3 ${
                    isDarkMode ? 'text-purple-400' : 'text-purple-700'
                  }`}>
                    Search Queries
                  </h3>
                  
                  <div className="space-y-2">
                    {extractedQueries.map((queryData, i) => (
                      <div key={`query-${i}`} className={`p-3 rounded-lg transition-colors ${
                        isDarkMode ? 'bg-gray-750 hover:bg-gray-700' : 'bg-white hover:bg-gray-100'
                      } border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} shadow-sm`}>
                        <div className="flex items-center space-x-2">
                          <Search className="w-4 h-4 text-purple-400 flex-shrink-0" />
                          <span className={`text-sm font-medium ${
                            isDarkMode ? 'text-white' : 'text-gray-800'
                          }`}>
                            {queryData.query}
                          </span>
                        </div>
                        
                        <div className="flex items-center justify-between mt-3 text-xs">
                          <span className={
                            isDarkMode ? 'text-purple-300' : 'text-purple-600'
                          }>
                            {queryData.resultsCount} {queryData.resultsCount === 1 ? 'result' : 'results'}
                          </span>
                          
                          <span className={`${
                            isDarkMode ? 'text-gray-500' : 'text-gray-500'
                          }`}>
                            {new Date(queryData.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    No search queries recorded for this research
                  </span>
                </div>
              )}
              
              {/* Source type distribution */}
              <div>
                <h3 className={`text-sm font-medium mb-3 ${
                  isDarkMode ? 'text-blue-400' : 'text-blue-700'
                }`}>
                  Source Distribution
                </h3>
                
                <div className="grid grid-cols-2 gap-2">
                  {typeCounts.map(({ type, count }) => (
                    <div key={type} className={`px-3 py-2 rounded-lg ${
                      isDarkMode ? 'bg-gray-750' : 'bg-white'
                    } border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <div className="flex items-center space-x-2">
                        <div className={`w-4 h-4 rounded-full ${
                          type === 'WEB' ? 'bg-blue-500/20' :
                          type === 'NEWS' ? 'bg-yellow-500/20' :
                          type === 'MARKET_DATA' ? 'bg-green-500/20' :
                          type === 'TECHNICAL' ? 'bg-purple-500/20' :
                          type === 'FUNDAMENTAL' ? 'bg-orange-500/20' :
                          'bg-gray-500/20'
                        } flex items-center justify-center`}>
                          {type === 'WEB' && <Globe className="w-3 h-3 text-blue-400" />}
                          {type === 'NEWS' && <FileText className="w-3 h-3 text-yellow-400" />}
                          {type === 'MARKET_DATA' && <TrendingUp className="w-3 h-3 text-green-400" />}
                          {type === 'TECHNICAL' && <LineChart className="w-3 h-3 text-purple-400" />}
                          {type === 'FUNDAMENTAL' && <BarChart3 className="w-3 h-3 text-orange-400" />}
                        </div>
                        <span className={`text-xs font-medium ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          {type === 'WEB' ? 'Web Sources' :
                           type === 'NEWS' ? 'News Sources' :
                           type === 'MARKET_DATA' ? 'Market Data' :
                           type === 'TECHNICAL' ? 'Technical Analysis' :
                           type === 'FUNDAMENTAL' ? 'Fundamentals' :
                           'Other Sources'}
                        </span>
                      </div>
                      <div className={`text-lg font-semibold mt-1 ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        {count}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SuggestedNextSteps: React.FC<{ steps: string[] }> = ({ steps }) => {
  const { isDarkMode } = useTheme();
  
  if (!steps || steps.length === 0) return null;
  
  return (
    <div className={`mt-4 rounded-lg p-4 ${
      isDarkMode ? 'bg-blue-500/10 border border-blue-500/30' : 'bg-blue-50 border border-blue-100'
    }`}>
      <h3 className={`text-sm font-medium mb-2 ${
        isDarkMode ? 'text-blue-400' : 'text-blue-700'
      }`}>
        Suggested Follow-up Questions
      </h3>
      <ul className="space-y-2">
        {steps.map((step, index) => (
          <li 
            key={index}
            className={`flex items-start ${
              isDarkMode ? 'text-blue-300' : 'text-blue-600'
            }`}
          >
            <ChevronRight className="w-4 h-4 mt-0.5 mr-1.5 flex-shrink-0" />
            <span className="text-sm">{step}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const FinancialDisclaimer: React.FC = () => {
  const { isDarkMode } = useTheme();
  
  return (
    <div className={`mt-4 text-xs px-3 py-2 rounded-lg ${
      isDarkMode ? 'bg-gray-300/10 border border-gray-100/30 text-gray-200' : 'bg-yellow-50 border border-yellow-200 text-yellow-800'
    }`}>
      <div className="flex items-start">
        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 mr-1.5 flex-shrink-0" />
        <span>
          Information provided is for educational purposes only, not financial advice. Market data may be delayed. 
          Always conduct your own research and consider consulting with a licensed financial advisor before making investment decisions.
        </span>
      </div>
    </div>
  );
};

// Extract tickers from research sources and details
const extractTickersFromResearch = (message: ChatMessage): string[] => {
  const tickers = new Set<string>();
  
  // Extract from research sources
  if (message.researchSources) {
    message.researchSources.forEach(source => {
      // Extract tickers directly mentioned in the research source
      if (source.tickers && Array.isArray(source.tickers)) {
        source.tickers.forEach(ticker => {
          if (ticker && typeof ticker === 'string' && ticker.length <= 5) {
            tickers.add(ticker);
          }
        });
      }
    });
  }
  
  // Extract from research progress details
  if (message.progressUpdate?.details?.tickers && 
      Array.isArray(message.progressUpdate.details.tickers)) {
    message.progressUpdate.details.tickers.forEach((ticker: string) => {
      if (ticker && typeof ticker === 'string' && ticker.length <= 5) {
        tickers.add(ticker);
      }
    });
  }

  // Extract from charts data
  if (message.charts) {
    Object.keys(message.charts).forEach(ticker => {
      if (ticker && typeof ticker === 'string' && ticker.length <= 5) {
        tickers.add(ticker);
      }
    });
  }
  
  // Add fallback to text parsing if no tickers found
  if (tickers.size === 0) {
    // Check for ticker patterns in content
    const tickerRegex = /\$?([A-Z]{1,5})\b/g;
    const commonWords = new Set(['I', 'EV', 'A', 'AI', 'U', 'S', 'THE', 'AND', 'OR', 'BUT', 'IF', 'IN', 'ON', 'AT', 'CEO', 'CFO', 'EPS', 'FED', 'AI', 'US']);
    
    const matches = message.content.match(tickerRegex) || [];
    const potentialTickers = matches.map(m => m.replace('$', ''));
    
    potentialTickers.forEach(ticker => {
      if (!commonWords.has(ticker) && ticker.length >= 1 && ticker.length <= 5) {
        tickers.add(ticker);
      }
    });
  }
  
  return Array.from(tickers);
};

const MessageBlock: React.FC<MessageBlockProps> = ({ message, styles, tickers, formatTimestamp }) => {
  const { isDarkMode } = useTheme();
  
  // Add state for tracking message hover and copy status
  const [isMessageHovered, setIsMessageHovered] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isLoadingStocks, setIsLoadingStocks] = useState(false);
  
  // Fix timestamp handling
  const timestamp = message.timestamp 
    ? typeof message.timestamp === 'string' 
      ? new Date(message.timestamp) 
      : message.timestamp
    : null;

  // Animation variants for consistent animations
  const messageAnimations = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  };

  // Copy message content to clipboard
  const handleCopyMessage = () => {
    navigator.clipboard.writeText(message.content)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      })
      .catch(err => console.error('Failed to copy message:', err));
  };

  // Determine when to show stock loading state
  useEffect(() => {
    // Get the display tickers
    let displayTickers: string[] = [];
    
    if (message.suggestedTickers && message.suggestedTickers.length > 0) {
      displayTickers = message.suggestedTickers.map(ticker => ticker.symbol);
    } else {
      const researchTickers = extractTickersFromResearch(message);
      const propTickers = tickers || [];
      displayTickers = [...new Set([...researchTickers, ...propTickers])];
    }
    
    // Show loading state if we have tickers to display
    if (displayTickers.length > 0) {
      setIsLoadingStocks(true);
      
      // Hide loading state after 10 seconds maximum (fallback)
      const timer = setTimeout(() => {
        setIsLoadingStocks(false);
      }, 10000);
      
      return () => clearTimeout(timer);
    }
    
    return () => {};
  }, [message, tickers]);

  // Function to handle stock data loaded
  const handleStockDataLoaded = useCallback(() => {
    setIsLoadingStocks(false);
  }, []);

  const renderChips = () => {
    const components = [];
    
    if (message.chips?.prompt) {
      components.push(
        <PromptChip
          key="prompt-chip"
          text={message.chips.prompt.text}
          prefix={message.chips.prompt.prefix}
          onRemove={() => {}} // Static in message display
          isDarkMode={isDarkMode}
          readOnly={true} // Add readOnly flag to hide X button in submitted messages
        />
      );
    }
    
    if (message.chips?.stock) {
      components.push(
        <StockChip
          key="stock-chip"
          ticker={message.chips.stock.ticker}
          companyName={message.chips.stock.companyName}
          prefix={message.chips.stock.prefix}
          onTickerClick={() => {}} // Use onTickerClick as that's what the component expects
          isDarkMode={isDarkMode}
          readOnly={true} // Add readOnly flag to hide X button in submitted messages
        />
      );
    }
    
    if (message.chips?.files && message.chips.files.length > 0) {
      message.chips.files.forEach((file, index) => {
        components.push(
          <div 
            key={`file-chip-${index}`}
            className={`inline-flex items-center gap-1 py-1.5 px-2.5 rounded-md text-sm mr-2 mb-2 ${
              isDarkMode ? 'bg-purple-900/40 text-purple-300 border border-purple-700/50' : 
                        'bg-purple-100 text-purple-800 border border-purple-200'
            }`}
          >
            <Paperclip className={`w-4 h-4 mr-1 ${
              isDarkMode ? 'text-purple-300' : 'text-purple-600'
            }`} />
            <span className="text-sm">{file.name}</span>
          </div>
        );
      });
    }
    
    return components.length > 0 ? (
      <div className="mb-3 flex flex-wrap gap-2">
        {components}
      </div>
    ) : null;
  };

  // Function to extract tickers mentioned in research sources
  const extractTickersFromResearch = (message: ChatMessage): string[] => {
    const tickers = new Set<string>();
    
    // Check in research sources
    if (message.researchSources) {
      message.researchSources.forEach(source => {
        if (source.tickers) {
          source.tickers.forEach(ticker => tickers.add(ticker));
        }
      });
    }
    
    // Check in message content for ticker patterns
    if (message.content) {
      const tickerRegex = /\$?([A-Z]{1,5})\b/g;
      const commonWords = new Set(['I', 'EV', 'A', 'AI', 'U', 'S', 'THE', 'AND', 'OR', 'BUT', 'IF', 'IN', 'ON', 'AT', 'CEO', 'CFO', 'EPS', 'FED', 'AI', 'US']);
      
      const matches = message.content.match(tickerRegex) || [];
      const potentialTickers = matches.map(m => m.replace('$', ''));
      
      potentialTickers.forEach(ticker => {
        if (!commonWords.has(ticker) && ticker.length >= 1 && ticker.length <= 5) {
          tickers.add(ticker);
        }
      });
    }
    
    return Array.from(tickers);
  };

  // Determine if there's additional text beyond the chip content
  const getDisplayContent = () => {
    if (!message.chips) return message.content;
    
    // Get original content
    let content = message.content;
    
    // Remove prefixes and primary content from chips
    if (message.chips.prompt) {
      content = content.replace(message.chips.prompt.prefix, '');
      content = content.replace(message.chips.prompt.text, '');
    }
    if (message.chips.stock) {
      content = content.replace(message.chips.stock.prefix, '');
      content = content.replace(`Tell me about ${message.chips.stock.ticker} stock`, '');
    }
    
    // Clean up by removing any leading colons, spaces, etc.
    content = content.replace(/^[:\s]+/, '');
    
    return content;
  };

  return (
    <motion.div
      className={`flex flex-col ${message.role === 'user' ? 'items-end' : 'items-start'} my-6 ml-4 sm:ml-6 md:ml-9 ${message.role === 'assistant' ? 'mr-4 sm:mr-12' : 'mr-8'}`}
      onMouseEnter={() => setIsMessageHovered(true)}
      onMouseLeave={() => setIsMessageHovered(false)}
    >
      {message.role === 'user' ? (
        // User messages: Show in bubble with narrower width
        <div className={`max-w-xl tracking-normal rounded-xl px-6 py-5 ${
          isDarkMode 
            ? 'bg-gray-800/60 border border-gray-700/80 text-white shadow-sm backdrop-blur-sm' 
            : 'bg-white border border-blue-200 text-gray-800 shadow-sm'
        }`}>
          {/* Render special chips if available */}
          {renderChips()}
          
          {/* Only render additional text if there's content beyond the chip */}
          {getDisplayContent().trim().length > 0 && (
            <p>{getDisplayContent()}</p>
          )}
          
          {/* Time indicator */}
          {timestamp && (
            <div className={`text-xs mt-2 flex justify-end ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {formatTimestamp(timestamp)}
            </div>
          )}
        </div>
      ) : (
        // Assistant messages: Show directly on background with wider width
        <div className="w-full max-w-5xl">
          {/* Assistant message header with copy button - use relative positioning */}
          <div className="flex justify-between items-center mb-2 relative">
            <div className="flex items-center">
              {timestamp && (
                <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  {formatTimestamp(timestamp)}
                </span>
              )}
            </div>
            
            {/* Copy button - Make it absolutely positioned to not affect layout */}
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: isMessageHovered ? 1 : 0 }}
              transition={{ duration: 0.15 }}
              onClick={handleCopyMessage}
              className={`absolute right-0 p-1.5 rounded-md ${
                isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
              }`}
              title="Copy message"
              style={{ pointerEvents: isMessageHovered ? 'auto' : 'none' }}
            >
              {isCopied ? (
                <Check className="w-4 h-4 text-green-500" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </motion.button>
          </div>
          
          {/* Assistant message content */}
          <div className="py-1 space-y-4">
            {message.isStreaming ? (
              <div className="flex items-center">
                <div className="text-sm leading-relaxed">{message.content}</div>
                <div className="ml-2"><StreamingIndicator /></div>
              </div>
            ) : (
              <ReactMarkdown
                className="prose dark:prose-invert max-w-none text-sm leading-relaxed break-words"
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                  a: ({ node, ...props }) => (
                    <a 
                      {...props} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-blue-600 dark:text-blue-400 hover:underline"
                    />
                  ),
                  
                  td: ({ node, ...props }) => (
                    <td 
                      {...props} 
                      className="px-4 py-2 text-sm"
                    />
                  )
                }}
              >
                {message.content}
              </ReactMarkdown>
            )}

            {message.isStreaming && (
              <div className="flex items-center mt-3 space-x-1">
                <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
                <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse delay-75"></div>
                <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse delay-150"></div>
              </div>
            )}
            
            {/* Show suggested follow-up questions if available */}
            {message.suggestedNextSteps && message.suggestedNextSteps.length > 0 && (
              <SuggestedNextSteps steps={message.suggestedNextSteps} />
            )}

            {/* Add subtle divider before stock tickers section */}
            <div className={`mt-6 mb-4 ${
              (() => {
                // Priority 1: Use AI-suggested tickers when available
                let displayTickers: string[] = [];
                
                if (message.suggestedTickers && message.suggestedTickers.length > 0) {
                  // Use AI-suggested tickers
                  displayTickers = message.suggestedTickers.map(ticker => ticker.symbol);
                } else {
                  // Fallback: Get tickers from research sources
                  const researchTickers = extractTickersFromResearch(message);
                  
                  // Use provided tickers as final fallback
                  const propTickers = tickers || [];
                  
                  // Combine both sources and deduplicate
                  displayTickers = [...new Set([...researchTickers, ...propTickers])];
                }
                
                return displayTickers.length > 0 ? 'block' : 'hidden';
              })()
            }`}>
              <div className={`h-px w-full ${
                isDarkMode ? 'bg-gray-700/70' : 'bg-gray-200/70'
              }`}></div>
            </div>

            {/* Add the stock tickers section */}
            {(() => {
              // Priority 1: Use AI-suggested tickers when available
              let displayTickers: string[] = [];
              
              if (message.suggestedTickers && message.suggestedTickers.length > 0) {
                // Use AI-suggested tickers
                displayTickers = message.suggestedTickers.map(ticker => ticker.symbol);
              } else {
                // Fallback: Get tickers from research sources
                const researchTickers = extractTickersFromResearch(message);
                
                // Use provided tickers as final fallback
                const propTickers = tickers || [];
                
                // Combine both sources and deduplicate
                displayTickers = [...new Set([...researchTickers, ...propTickers])];
              }
              
              // Show loading spinner when tickers exist but no data yet
              if (displayTickers.length > 0 && isLoadingStocks) {
                return (
                  <div className="flex flex-col items-center p-6 border rounded-lg border-dashed mb-6
                    border-gray-400/30 bg-gray-800/20">
                    <div className="w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Loading stock data...
                    </p>
                  </div>
                );
              }
              
              return displayTickers.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {displayTickers.map((ticker) => {
                    // For AI-suggested tickers, try to find company name and relevance
                    const suggestedTicker = message.suggestedTickers?.find(t => t.symbol === ticker);
                    const companyName = suggestedTicker?.name;
                    const relevance = suggestedTicker?.relevance;
                    
                    // Format prefix based on relevance
                    let prefix = "";
                    if (relevance === "primary") prefix = "[Primary]";
                    else if (relevance === "secondary") prefix = "[Related]";
                    else if (relevance === "comparative") prefix = "[Comparison]";
                    else if (relevance === "sector") prefix = "[Sector]";
                    else if (relevance === "index") prefix = "[Index]";
                    
                    // Use EnhancedStockChip instead of InteractiveStockChip for the advanced chart functionality
                    return (
                      <EnhancedStockChip
                        key={ticker}
                        ticker={ticker}
                        companyName={companyName}
                        prefix={prefix}
                        isDarkMode={isDarkMode}
                        onDataLoaded={handleStockDataLoaded}
                      />
                    );
                  })}
                </div>
              ) : null;
            })()}

            {message.webSearchResults && message.webSearchResults.length > 0 && (
              <WebSearchResultsIndicator results={message.webSearchResults} />
            )}
            
            {message.researchSources && message.researchSources.length > 0 && (
              <ResearchSourcesIndicator sources={message.researchSources} />
            )}

            <FinancialDisclaimer />
          </div>
        </div>
      )}
    </motion.div>
  );
};

// StockChip component for message blocks
const StockChip: React.FC<{
  ticker: string;
  companyName?: string;
  prefix?: string;
  isDarkMode: boolean;
  onTickerClick: (ticker: string) => void;
  readOnly?: boolean;
}> = ({ ticker, companyName, prefix, isDarkMode, onTickerClick, readOnly = false }) => {
  const [imageError, setImageError] = useState(false);
  
  return (
    <div 
      onClick={() => onTickerClick(ticker)}
      className={`inline-flex items-center gap-1 py-1 px-2 rounded-md text-sm cursor-pointer ${
        isDarkMode ? 'bg-green-900/40 text-green-300 border border-green-700/50 hover:bg-green-800/50' : 
                    'bg-green-100 text-green-800 border border-green-200 hover:bg-green-200/70'
      }`}
    >
      {/* Company logo or fallback */}
      <div className={`overflow-hidden w-5 h-5 flex-shrink-0 rounded-full flex items-center justify-center 
        ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'} 
        border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} mr-1`}
      >
        {!imageError ? (
          <img
            src={`/api/market/company-logo/${ticker}`}
            alt={`${ticker} logo`}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <span className={`text-xs font-medium ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            {ticker[0]}
          </span>
        )}
      </div>
      
      {prefix && (
        <span className={`text-xs px-1.5 py-0.5 rounded ${
          isDarkMode ? 'bg-green-800/70 text-green-300' : 'bg-green-200 text-green-700'
        }`}>
          {prefix.replace(/[\[\]]/g, '')}
        </span>
      )}
      
      <div className="flex flex-col">
        <span className="font-medium">{ticker}</span>
        {companyName && <span className="text-xs break-normal">{companyName}</span>}
      </div>
    </div>
  );
};

export default MessageBlock;
